

import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { Card } from "@/components/Card/Card";
import { Spacing } from "@/components/Spacing/Spacing";
import { FormInput } from "@/components/Inputs/FormInput";
import { SliderInput } from "@/components/Inputs/SliderInput";
import { motion } from "framer-motion";
import { SHEET_INITIAL, SHEET_ANIMATE, SHEET_EXIT, SHEET_SPRING } from "@/components/Modal/sheetMotion";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { createPortal } from "react-dom";
import { IconX } from "@tabler/icons-react";
import { INVALID_STRING_PATTERN } from "@/lib/utils/default-returner";
import { useSuppressCoach } from "@/lib/onboarding/useSuppressCoach";

interface PDuplicateEventProps {
  eventName: string;
  onCloseAction: (e: React.MouseEvent) => void;
  onConfirmAction: (name: string, copyGuests: boolean) => Promise<void>;
  isLoading: boolean;
  error?: string;
}

export const PDuplicateEvent: React.FC<PDuplicateEventProps> = ({
  eventName,
  onCloseAction,
  onConfirmAction,
  isLoading,
  error,
}) => {
  useSuppressCoach(); // hide bottom-right floaters over this modal
  const t = useTranslations("duplicateEvent");
  const [name, setName] = useState(eventName);
  const [copyGuests, setCopyGuests] = useState(false);
  const normalizedName = name.trim();
  const isNameValid =
    normalizedName.length > 0 &&
    normalizedName.length <= 70 &&
    !INVALID_STRING_PATTERN.test(normalizedName);

  const submitOnEnter = () => {
    if (!isLoading && isNameValid) onConfirmAction(normalizedName, copyGuests);
  };

  const modalContent = (
    <motion.div
      className="fixed left-0 top-0 bg-black/50 flex items-end sm:items-center justify-center sm:pr-[var(--ai-rail-push)] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none h-dvh w-screen z-50 cursor-default"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCloseAction(e);
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="w-full sm:w-auto flex justify-center"
        initial={SHEET_INITIAL}
        animate={SHEET_ANIMATE}
        exit={SHEET_EXIT}
        transition={SHEET_SPRING}
      >
        <Card
          padding="p-6"
          width="w-full sm:w-[95vw] sm:max-w-125"
          rounded="rounded-t-3xl sm:rounded-3xl"
          shadow="shadow-xl"
          header={t("title")}
          icon={IconX}
          iconActionOnClick={onCloseAction}
        >
          <div className="flex flex-col items-center w-full">
            <Spacing height="h-6" />
            <div className="w-full">
              <FormInput
                id="duplicate-event-name"
                data-testid="root-duplicate-name-input"
                placeholder={t("namePlaceholder")}
                value={name}
                onChangeAction={setName}
                width="w-full"
                onEnterAction={submitOnEnter}
                maxLength={70}
              />
            </div>

            {error && (
              <>
                <Spacing height="h-4" />
                <p className="body-2-regular font-error w-full">{error}</p>
              </>
            )}

            <Spacing height="h-6" />
            <div className="w-full flex flex-col gap-1.5">
              <SliderInput
                label={t("copyGuestsLabel")}
                checked={copyGuests}
                setChecked={() => setCopyGuests((prev) => !prev)}
                hideInfoIcon
                testId="duplicate-copy-guests"
              />
              {copyGuests && (
                <p className="body-4-regular font-tertiary">
                  {t("copyGuestsHint")}
                </p>
              )}
            </div>

            <Spacing height="h-6" />
            <p className="body-3-regular font-secondary w-full">
              {t("disclaimerCopyScope")}
            </p>

            <Spacing height="h-6" />
            <div className="flex flex-row w-full space-x-3">
              <SecondaryButton
                text={t("cancel")}
                onClick={(e) => onCloseAction(e)}
                disabled={isLoading}
                width="w-1/2"
              />
              <PrimaryButton
                loading={isLoading}
                text={t("confirm")}
                onClick={() => { if (isNameValid) onConfirmAction(normalizedName, copyGuests); }}
                width="w-1/2"
                disabled={!isNameValid || isLoading}
                testId="root-duplicate-submit"
              />
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
};
